import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { fmtGHS } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Truck, ClipboardCheck, Scale, Check } from "lucide-react";
import { toast } from "sonner";
import { BASIS_LABEL } from "./common";

const Step = ({ n, title, done, sub, active }) => (
  <div className={`flex-1 rounded-md border p-2.5 ${done ? "border-emerald-500/40 bg-emerald-500/5" : active ? "border-amber-500/50 bg-amber-500/5" : "border-border"}`} data-testid={`ws-step-${n}`}>
    <div className="flex items-center gap-1.5 text-xs font-medium">{done ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <span className="font-mono">{n}</span>} {title}</div>
    <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>
  </div>
);

export const DeliverySteps = ({ o }) => (
  <div className="flex flex-col sm:flex-row gap-2" data-testid="ws-delivery-steps">
    <Step n={1} title="Supplier dispatch" done={!!o.dispatched_at} active={o.status === "pending"} sub={o.dispatched_at ? `by ${o.dispatched_by}` : "Awaiting supplier"} />
    <Step n={2} title="Hospital receipt" done={!!o.received_at} active={o.status === "dispatched"} sub={o.received_at ? `${o.hospital_confirmed_by || "confirmed"} · logged by ${o.received_by}` : "Awaiting hospital"} />
    {(o.discrepancy_id || o.status === "pending_review") && <Step n={3} title="Discrepancy review" done={!!o.resolution} active={o.status === "pending_review"} sub={o.resolution ? BASIS_LABEL[o.resolution.basis] : "Held — not counted against allocation"} />}
  </div>
);

const QtyGrid = ({ o, qty, setQty, compare }) => (
  <table className="w-full text-sm">
    <thead className="text-left text-xs text-muted-foreground"><tr><th className="py-1">Product</th><th className="py-1 text-right">Ordered</th>{compare && <th className="py-1 text-right">Dispatched</th>}<th className="py-1 text-right w-28">{compare ? "Received" : "Dispatched"}</th></tr></thead>
    <tbody>{o.lines.map((l) => {
      const v = qty[l.product_id];
      const off = Number(v) !== l.quantity || (compare && Number(v) !== l.dispatched_qty);
      return (
        <tr key={l.product_id}>
          <td className="py-1">{l.generic_name} <span className="text-xs font-mono text-muted-foreground">{l.sku}</span></td>
          <td className="py-1 text-right font-mono">{l.quantity}</td>
          {compare && <td className="py-1 text-right font-mono">{l.dispatched_qty}</td>}
          <td className="py-1 text-right"><Input type="number" min={0} className={`h-8 w-24 ml-auto text-right ${off ? "border-amber-500" : ""}`} value={v} onChange={(e) => setQty({ ...qty, [l.product_id]: e.target.value })} data-testid={`ws-qty-${l.sku || l.product_id}`} /></td>
        </tr>
      );
    })}</tbody>
  </table>
);

const ConfirmForm = ({ o, receive, onDone }) => {
  const [qty, setQty] = useState(Object.fromEntries(o.lines.map((l) => [l.product_id, receive ? l.dispatched_qty : l.quantity])));
  const [ref, setRef] = useState(receive ? "" : o.supplier_ref || "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const bad = Object.values(qty).some((v) => v === "" || Number(v) < 0 || !Number.isInteger(Number(v)));
  const submit = async () => {
    setBusy(true);
    const lines = o.lines.map((l) => ({ product_id: l.product_id, quantity: Number(qty[l.product_id]) }));
    const body = receive ? { lines, confirmed_by: ref, note } : { lines, supplier_ref: ref, note };
    try {
      const { data } = await api.post(`/wholesale/orders/${o.id}/${receive ? "receive" : "dispatch"}`, body);
      toast[data.status === "pending_review" ? "warning" : "success"](
        !receive ? `${data.code}: supplier dispatch logged` : data.status === "pending_review" ? `${data.code}: quantities differ — held for review` : `${data.code} fulfilled — ${fmtGHS(data.total_cost)} counted against ${data.hospital_name}'s allocation`);
      onDone(data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-md border border-border p-3 space-y-3" data-testid={receive ? "ws-receive-form" : "ws-dispatch-form"}>
      <div className="text-sm font-medium">{receive ? "Step 2 — What the hospital partner confirmed they received" : "Step 1 — What the supplier dispatched"}</div>
      <QtyGrid o={o} qty={qty} setQty={setQty} compare={receive} />
      <div className="grid sm:grid-cols-2 gap-2">
        <div><Label className="text-xs">{receive ? "Confirmed by (hospital contact)" : "Supplier ref / waybill"}</Label><Input value={ref} onChange={(e) => setRef(e.target.value)} data-testid={receive ? "ws-receive-confirmed-by" : "ws-dispatch-ref"} /></div>
        <div><Label className="text-xs">Note</Label><Input value={note} onChange={(e) => setNote(e.target.value)} data-testid={receive ? "ws-receive-note" : "ws-dispatch-note"} /></div>
      </div>
      <Button disabled={busy || bad} onClick={submit} data-testid={receive ? "ws-receive-submit" : "ws-dispatch-submit"}>{busy ? "Saving…" : receive ? "Confirm hospital receipt" : "Confirm supplier dispatch"}</Button>
    </div>
  );
};

const ResolveForm = ({ o, onDone }) => {
  const [basis, setBasis] = useState("received");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const key = { received: "received_qty", dispatched: "dispatched_qty", ordered: "quantity" }[basis];
  const value = key ? o.lines.reduce((s, l) => s + l.unit_cost * l[key], 0) : 0;
  const submit = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/wholesale/orders/${o.id}/resolve`, { basis, note }); toast.success(`${data.code} discrepancy resolved`); onDone(data); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-md border border-rose-500/40 bg-rose-500/5 p-3 space-y-3" data-testid="ws-resolve-form">
      <div className="text-sm font-medium flex items-center gap-1.5"><Scale className="h-4 w-4" /> Resolve discrepancy</div>
      <div className="grid sm:grid-cols-2 gap-2">
        <div><Label className="text-xs">Resolution</Label>
          <Select value={basis} onValueChange={setBasis}><SelectTrigger className="h-9" data-testid="ws-resolve-basis"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(BASIS_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{k === "cancel" ? "Cancel the order" : v.replace("Accepted", "Accept")}</SelectItem>)}</SelectContent></Select></div>
        <div><Label className="text-xs">How was it resolved? (required)</Label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. supplier credited 2 units short" data-testid="ws-resolve-note" /></div>
      </div>
      <div className="text-xs text-muted-foreground" data-testid="ws-resolve-value">{key ? `${fmtGHS(value)} will count against the allocation` : "Nothing will count against the allocation"}</div>
      <Button disabled={busy || note.trim().length < 3} onClick={submit} data-testid="ws-resolve-submit">{busy ? "Saving…" : "Resolve & close review"}</Button>
    </div>
  );
};

export const VirtualDeliveryPanel = ({ o, can, onDone }) => {
  const [open, setOpen] = useState(false);
  if (o.status === "pending_review") return can("wholesale.accounts") ? <ResolveForm o={o} onDone={onDone} /> : <div className="text-xs text-rose-600" data-testid="ws-review-wait">Held for review by a user who manages hospital partners.</div>;
  if (!["pending", "dispatched"].includes(o.status) || !can("wholesale.fulfil")) return null;
  const receive = o.status === "dispatched";
  if (open) return <ConfirmForm key={o.status} o={o} receive={receive} onDone={(d) => { setOpen(false); onDone(d); }} />;
  return (
    <Button variant="outline" onClick={() => setOpen(true)} data-testid={receive ? "ws-open-receive-btn" : "ws-open-dispatch-btn"}>
      {receive ? <ClipboardCheck className="h-4 w-4 mr-1.5" /> : <Truck className="h-4 w-4 mr-1.5" />}{receive ? "Log hospital receipt" : "Log supplier dispatch"}
    </Button>
  );
};
